/**
 * /tenants/$slug/settings — layout route.
 *
 * Renders the breadcrumb + page header (status badge, archetype
 * subtitle, suspended banner) + tab strip + Outlet. Each settings
 * section is its own sibling route; navigating between them is a
 * proper URL change (deep-linkable, back-button-friendly,
 * restorable on refresh) — never local state.
 *
 * The shell also owns the dirty-state context that every child
 * page reports into, so the tab strip can render unsaved-changes
 * dots and the page can guard accidental navigation away.
 */

import { fetchTenantDetail } from "@/features/tenants/queries";
import { ForbiddenError } from "@/lib/api-client";
import { useState } from "react";
import { createFileRoute, Outlet, useRouter } from "@tanstack/react-router";
import { AlertBanner, Breadcrumbs, Button } from "@pureid/ui";
import { SettingsTabs } from "@/features/tenant-settings/components/settings-tabs";
import { SettingsHeader } from "@/features/tenant-settings/components/settings-header";
import {
  DirtyStateProvider,
  useDirtyTabs,
} from "@/features/tenant-settings/components/dirty-state";
import { useAdmin } from "@/queries/session";
import { fetchAutomatedOnboardingCapability } from "@/features/product-areas/queries";
import { queryKeys } from "@/lib/query-keys";

function SettingsLayout() {
  const { slug } = Route.useParams();
  const { settingsTenant: tenant } = Route.useRouteContext();
  const admin = useAdmin();
  const { automatedOnboardingAvailable, automatedOnboardingLookupFailed } = Route.useLoaderData();
  const router = useRouter();
  const [retryingCapability, setRetryingCapability] = useState(false);
  async function retryCapability() {
    setRetryingCapability(true);
    try {
      await router.invalidate();
    } catch {
      // The warning remains visible; unrelated route-loader failures keep
      // their own error boundary rather than becoming onboarding copy here.
    } finally {
      setRetryingCapability(false);
    }
  }
  return (
    <DirtyStateProvider>
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            // The cross-tenant list is a platform surface — for a
            // tenant admin the trail starts at their own tenant.
            ...(admin.isPlatformAdmin ? [{ label: "Tenants", href: "/tenants" }] : []),
            { label: tenant.name, href: `/tenants/${slug}` },
            { label: "Settings" },
          ]}
        />
        {/* Lifecycle actions (suspend / reactivate / delete) are
            platform-tree-only endpoints — a tenant admin cannot
            suspend or delete their own tenant (deny-by-default on
            the tenant-host tree), so the controls require
            isPlatformAdmin on top of the permission. */}
        <SettingsHeader
          tenant={tenant}
          canManageLifecycle={admin.isPlatformAdmin && admin.can("tenants.write")}
        />
        {automatedOnboardingLookupFailed && (
          <AlertBanner variant="warning" title="Automated onboarding availability could not be checked">
            Unrelated settings remain available. Automated onboarding stays hidden until the product
            check succeeds.
            <Button
              className="ml-3"
              size="sm"
              variant="outline"
              disabled={retryingCapability}
              onClick={() => void retryCapability()}
            >
              {retryingCapability ? "Retrying…" : "Retry"}
            </Button>
          </AlertBanner>
        )}
        <TabStrip
          tenantSlug={slug}
          showDangerZone={admin.isPlatformAdmin && admin.can("tenants.write")}
          showAutomatedOnboarding={admin.can("applications.manage") && automatedOnboardingAvailable}
          showProductAreas={admin.can("product_areas.read")}
        />
        <div className="pt-2">
          <Outlet />
        </div>
      </div>
    </DirtyStateProvider>
  );
}

// Tabs render in their own component so the dirty-state subscription
// only re-runs them — the rest of the layout stays static.
function TabStrip(props: {
  tenantSlug: string;
  showDangerZone: boolean;
  showAutomatedOnboarding: boolean;
  showProductAreas: boolean;
}) {
  const dirtyTabs = useDirtyTabs();
  return <SettingsTabs {...props} dirtyTabs={dirtyTabs} />;
}

export const Route = createFileRoute("/_authed/tenants/$slug/settings")({
  beforeLoad: async ({ context }) => {
    if (!context.authority.permissions.includes("tenant_settings.read")) throw new ForbiddenError(null, null, "Your service assignment does not include tenant settings.");
    const settingsTenant = await context.queryClient.fetchQuery({ queryKey: queryKeys.tenants.detail(context.tenant.slug), queryFn: () => fetchTenantDetail(context.tenant.id), staleTime: 0 });
    return { settingsTenant };
  },
  loader: async ({ context }) => {
    if (!context.authority.permissions.includes("applications.manage")) {
      return { automatedOnboardingAvailable: false, automatedOnboardingLookupFailed: false };
    }
    try {
      const result = await context.queryClient.ensureQueryData({
        queryKey: queryKeys.productAreas.automatedOnboarding(context.tenant.id),
        queryFn: () => fetchAutomatedOnboardingCapability(context.tenant.id),
      });
      return {
        automatedOnboardingAvailable: result.data.availability !== "hidden",
        automatedOnboardingLookupFailed: false,
      };
    } catch {
      return { automatedOnboardingAvailable: false, automatedOnboardingLookupFailed: true };
    }
  },
  component: SettingsLayout,
});
